import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { baseUrl } from '../../constants';

@Injectable({ providedIn: 'root' })
export class ProjectManagementService {
  private apiUrl = `${baseUrl}ProjectManagement`;

  constructor(private http: HttpClient) {}

  private get headers() {
    const token = localStorage.getItem('token');
    return {
      headers: new HttpHeaders({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      })
    };
  }

  getProjects(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/projects`, this.headers);
  }

  getProject(projectId: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/projects/${projectId}`, this.headers);
  }

  createProject(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/projects`, payload, this.headers);
  }

  updateProject(projectId: number, payload: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/projects/${projectId}`, payload, this.headers);
  }

  getProjectTeam(projectId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/projects/${projectId}/team`, this.headers);
  }

  assignProjectTeam(projectId: number, members: Array<{ staffId: number; roleInProject?: string }>): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/projects/${projectId}/team`, members || [], this.headers);
  }

  getProjectTasks(projectId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/projects/${projectId}/tasks`, this.headers);
  }

  getTaskWorkspace(filters?: { status?: string }): Observable<any[]> {
    let params = new HttpParams();
    if (filters?.status && filters.status !== 'All' && filters.status !== 'all') {
      params = params.set('status', filters.status);
    }

    return this.http.get<any[]>(`${this.apiUrl}/tasks`, { ...this.headers, params });
  }

  createProjectTask(projectId: number, payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/projects/${projectId}/tasks`, payload, this.headers);
  }

  createTask(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/tasks`, payload, this.headers);
  }

  updateProjectTask(taskId: number, payload: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/tasks/${taskId}`, payload, this.headers);
  }

  getTaskComments(taskId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/tasks/${taskId}/comments`, this.headers);
  }

  addTaskComment(taskId: number, payload: {
    comment: string;
    parentCommentId?: number | null;
    mediaAttachments?: any[];
    mentionedStaffIds?: number[];
  }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/tasks/${taskId}/comments`, payload, this.headers);
  }

  getProjectOnboarding(projectId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/projects/${projectId}/onboarding`, this.headers);
  }

  getProjectKnowledgeWorkspace(projectId: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/projects/${projectId}/knowledge`, this.headers);
  }

  createProjectKnowledgeFolder(projectId: number, payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/projects/${projectId}/knowledge/folders`, payload, this.headers);
  }

  updateProjectKnowledgeFolder(folderId: number, payload: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/knowledge/folders/${folderId}`, payload, this.headers);
  }

  createProjectKnowledgeDocument(projectId: number, payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/projects/${projectId}/knowledge/documents`, payload, this.headers);
  }

  updateProjectKnowledgeDocument(documentId: number, payload: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/knowledge/documents/${documentId}`, payload, this.headers);
  }

  createOnboardingItem(projectId: number, payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/projects/${projectId}/onboarding`, payload, this.headers);
  }

  updateOnboardingItem(itemId: number, payload: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/onboarding/${itemId}`, payload, this.headers);
  }

  getProjectRisks(projectId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/projects/${projectId}/risks`, this.headers);
  }

  createProjectRisk(projectId: number, payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/projects/${projectId}/risks`, payload, this.headers);
  }

  updateProjectRisk(riskId: number, payload: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/risks/${riskId}`, payload, this.headers);
  }

  getProjectAnalytics(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/analytics`, this.headers);
  }

  getProjectReport(filters: {
    from?: string;
    to?: string;
    projectId?: number | string | null;
    staffId?: number | string | null;
    taskStatus?: string;
    search?: string;
  }): Observable<any[]> {
    let params = new HttpParams();
    if (filters.from) params = params.set('from', filters.from);
    if (filters.to) params = params.set('to', filters.to);
    if (filters.projectId) params = params.set('projectId', String(filters.projectId));
    if (filters.staffId) params = params.set('staffId', String(filters.staffId));
    if (filters.taskStatus && filters.taskStatus !== 'all') params = params.set('taskStatus', filters.taskStatus);
    if (filters.search) params = params.set('search', filters.search);

    return this.http.get<any[]>(`${this.apiUrl}/report`, { ...this.headers, params });
  }

  getClientUsers(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/clients`, this.headers);
  }

  getMyTasks(filters?: { status?: string }): Observable<any[]> {
    let params = new HttpParams();
    if (filters?.status && filters.status !== 'all') params = params.set('status', filters.status);
    return this.http.get<any[]>(`${this.apiUrl}/my-tasks`, { ...this.headers, params });
  }

  getStaffs(): Observable<any[]> {
    return this.http.get<any[]>(`${baseUrl}Staffs`, this.headers);
  }
}
